import { useMemo, useState, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled/macro';
import { Flex, Heading, Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';

import {
    createOutages,
    getScheduledOutages,
    updateOutages,
    deleteOutages,
    deleteAllOutages,
    formatParsedNewScheduledOutages,
    getHistoricalOutput,
    createHistoricalOutput,
    updateHistoricalOutput,
    deleteAllHistoricalOutput,
    deleteHistoricalOutput,
    formatParsedNewHistoricalOutput,
} from '../../services/batteries';
import { getValidRowsWithDates } from '../../services/grid';

import BatteryTimeline from './BatteryTimeline';
import BatteryBiddingStrategy from './BatteryBiddingStrategy';

import SecondaryButton from '../utils/SecondaryButton';

import SubItemData from '../itemData/SubItemData';
import DownloadButton from '../itemData/DownloadButton';
import UploadButton from '../itemData/UploadButton';

import { ReactComponent as RefreshIcon } from '../../icons/refresh.svg';

const SubBatteryData = ({
    templateId,
    subBatteryId,
    ancillaries,
    peakPeriods,
    quantityTypes,
    templates,
    templateConfig,
    onTemplatesConfigChange,
}) => {
    const intl = useIntl();

    const [key, setKey] = useState(0);
    const [loadingQueue, setLoadingQueue] = useState([]); // unique keys represeting which resource is in loading state
    const [sorting, setSorting] = useState({ outage: {}, historicalOutput: {} });

    const refreshTabs = useCallback(() => {
        setKey((prev) => prev + 1);
        setSorting({ outage: {}, historicalOutput: {} }); // reset sorting for export on tab refresh
    }, []);

    const onLoadingStart = useCallback((key) => {
        setLoadingQueue((prev) => prev.concat(key));
    }, []);

    const onLoadingEnd = useCallback((key) => {
        setLoadingQueue((prev) => prev.filter((p) => p !== key));
    }, []);

    const scheduledOutagesGridResource = useMemo(() => {
        return {
            create: (rows) => createOutages(subBatteryId, rows),
            read: (pagination) => {
                setSorting((prev) => {
                    return {
                        ...prev,
                        outage: pagination,
                    };
                });

                return getScheduledOutages(subBatteryId, pagination);
            },
            update: (rows) => {
                const validRows = getValidRowsWithDates(rows, ['startDate', 'endDate']);

                return updateOutages(subBatteryId, validRows);
            },
            delete: (rows) =>
                deleteOutages(
                    subBatteryId,
                    rows.map((row) => row.generationOutageId)
                ),
            deleteAll: () => deleteAllOutages(subBatteryId),
        };
    }, [subBatteryId]);

    const scheduledOutagesColumnSchema = useMemo(() => {
        return {
            startDate: { name: 'startDate', type: 'date' },
            endDate: { name: 'endDate', type: 'date' },
            percentOutage: { name: 'percentOutage', type: 'number' },
        };
    }, []);

    const historicalOutputGridResource = useMemo(() => {
        return {
            create: (rows) => createHistoricalOutput(subBatteryId, rows),
            read: (pagination) => {
                setSorting((prev) => {
                    return {
                        ...prev,
                        historicalOutput: pagination,
                    };
                });

                return getHistoricalOutput(subBatteryId, pagination);
            },
            update: (rows) => {
                const validRows = getValidRowsWithDates(rows, ['eventDate']);

                return updateHistoricalOutput(subBatteryId, validRows);
            },
            delete: (rows) =>
                deleteHistoricalOutput(
                    subBatteryId,
                    rows.map((row) => row.id)
                ),
            deleteAll: () => deleteAllHistoricalOutput(subBatteryId),
        };
    }, [subBatteryId]);

    const historicalOutputColumnSchema = useMemo(() => {
        return {
            eventDate: { name: 'eventDate', type: 'date' },
            value: { name: 'value', type: 'number' },
            quantityTypeId: {
                name: 'quantityTypeId',
                type: 'string',
                isRequired: false,
                constraints: quantityTypes.map((type) => type.description),
            },
        };
    }, [quantityTypes]);

    const formatNewHistoricalOutput = useCallback(
        (data) => formatParsedNewHistoricalOutput(data, quantityTypes),
        [quantityTypes]
    );

    return (
        <Tabs px={0} mt={6} isLazy isManual lazyBehavior="keepMounted" key={key}>
            <StyledTabList w="100%" flexWrap={{ base: 'wrap', xl: 'nowrap' }} alignItems="center">
                <StyledTab>
                    <FormattedMessage id="common_historical_data_timeline_editor" />
                </StyledTab>

                <StyledTab>
                    <FormattedMessage id="common_historical_data_bidding_strategy" />
                </StyledTab>

                <StyledTab>
                    <FormattedMessage id="batteries_scheduled_outages_heading" />
                </StyledTab>

                <StyledTab>
                    <FormattedMessage id="batteries_historical_output_heading" />
                </StyledTab>

                <Flex ml="auto" order={{ base: -1, xl: 0 }} w={{ base: '100%', xl: 'auto' }}>
                    <DownloadButton
                        type="batteries"
                        itemId={subBatteryId}
                        hasLabel={true}
                        itemType="battery"
                        templateId={templateId}
                        batteryDataSorting={sorting}
                        mr={2}
                        w="auto !important"
                        bodyMessage={<FormattedMessage id="download_batteries_modal_explanation" />}
                    />

                    <UploadButton
                        type="batteries"
                        itemId={subBatteryId}
                        onTemplatesConfigChange={onTemplatesConfigChange}
                        hasLabel={true}
                        mr={4}
                        w="auto !important"
                    />

                    <SecondaryButton
                        onClick={refreshTabs}
                        size="sm"
                        variant="secondary"
                        leftIcon={<RefreshIcon />}
                        isLoading={loadingQueue.length > 0}
                        loadingText={intl.formatMessage({ id: 'common_loading' })}
                        textTransform="capitalize"
                        ml="auto"
                    >
                        <FormattedMessage id="common_refresh" />
                    </SecondaryButton>
                </Flex>
            </StyledTabList>

            <TabPanels mt={4}>
                <TabPanel>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="batteries_timeline_editor_heading" />
                    </Heading>

                    <BatteryTimeline
                        loadingQueue={loadingQueue}
                        onLoadingStart={onLoadingStart}
                        onLoadingEnd={onLoadingEnd}
                        subBatteryId={subBatteryId}
                        ancillaries={ancillaries}
                        peakPeriods={peakPeriods}
                    />
                </TabPanel>

                <TabPanel>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="batteries_bidding_strategy_heading" />
                    </Heading>

                    <BatteryBiddingStrategy
                        onLoadingStart={onLoadingStart}
                        onLoadingEnd={onLoadingEnd}
                        subBatteryId={subBatteryId}
                        ancillaries={ancillaries}
                        peakPeriods={peakPeriods}
                        templates={templates}
                        templateId={templateId}
                        templateConfig={templateConfig}
                        onTemplatesConfigChange={onTemplatesConfigChange}
                    />
                </TabPanel>

                <TabPanel>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="batteries_scheduled_outages_heading" />
                    </Heading>

                    <SubItemData
                        tab="2"
                        onLoadingStart={onLoadingStart}
                        onLoadingEnd={onLoadingEnd}
                        gridResource={scheduledOutagesGridResource}
                        columns={[
                            {
                                type: 'date',
                                field: 'startDate',
                                headerName: intl.formatMessage({ id: 'common_grid_start_date' }),
                                cellEditorParams: { nullable: true },
                                sortable: true,
                            },
                            {
                                type: 'date',
                                field: 'endDate',
                                headerName: intl.formatMessage({ id: 'common_grid_end_date' }),
                                cellEditorParams: { nullable: true },
                                sortable: true,
                            },
                            {
                                type: 'number',
                                field: 'percentOutage',
                                headerName: intl.formatMessage({
                                    id: 'batteries_scheduled_outages_percent_outage',
                                }),
                                sortable: true,
                            },
                        ]}
                        addRowsValidationSchema={scheduledOutagesColumnSchema}
                        formatNewRows={formatParsedNewScheduledOutages}
                    />
                </TabPanel>

                <TabPanel>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="batteries_historical_output_heading" />
                    </Heading>

                    <SubItemData
                        tab="3"
                        onLoadingStart={onLoadingStart}
                        onLoadingEnd={onLoadingEnd}
                        gridResource={historicalOutputGridResource}
                        columns={[
                            {
                                type: 'date',
                                field: 'eventDate',
                                headerName: intl.formatMessage({ id: 'batteries_historical_output_event_date' }),
                                cellEditorParams: { nullable: true },
                                sortable: true,
                            },
                            {
                                type: 'number',
                                field: 'value',
                                headerName: intl.formatMessage({
                                    id: 'batteries_historical_output_value',
                                }),
                                sortable: true,
                            },
                            {
                                field: 'quantityTypeId',
                                type: 'select',
                                cellEditorParams: { options: quantityTypes, required: false },
                                headerName: intl.formatMessage({ id: 'batteries_historical_output_quantity' }),
                            },
                        ]}
                        addRowsValidationSchema={historicalOutputColumnSchema}
                        formatNewRows={formatNewHistoricalOutput}
                    />
                </TabPanel>
            </TabPanels>
        </Tabs>
    );
};

// @todo duplicate styles in SubLoadData/SubRenewableData/SubBatteryData
const StyledTabList = styled(TabList)`
    border-bottom: 1px solid var(--chakra-colors-gray-200);

    > [aria-selected='true'] {
        border-bottom: 4px solid var(--chakra-colors-blue-400);
        color: var(--chakra-colors-black);
    }

    > [role='tab'] {
        color: var(--chakra-colors-black);

        &:hover {
            color: var(--chakra-colors-blue-400);
        }
    }
`;

const StyledTab = styled(Tab)`
    padding: 16px 0;
    margin-right: 24px;
    margin-bottom: 0;
    color: var(--chakra-colors-gray-600);
    text-transform: capitalize;
`;

export default SubBatteryData;
